import React from "react";
import {
  ArrowLeft,
  Plus,
  RefreshCw,
} from "lucide-react";

/**
 * EnglishQuizHeader
 *
 * Reusable header for English Quiz management screens.
 *
 * Props:
 * - title
 * - subtitle
 * - onCreate
 * - onRefresh
 * - onBack
 * - showCreate
 * - showRefresh
 * - isRefreshing
 * - createLabel
 * - refreshLabel
 * - children
 */
const EnglishQuizHeader = ({
  title = "English Quizzes",
  subtitle = "Manage and organize English quizzes",
  onCreate,
  onRefresh,
  onBack,
  showCreate = true,
  showRefresh = true,
  isRefreshing = false,
  createLabel = "Create Quiz",
  refreshLabel = "Refresh",
  children,
}) => {
  return (
    <div className="mb-6">
      <div className="flex flex-col gap-4 rounded-xl border border-gray-200 bg-white p-4 shadow-sm sm:p-5 lg:flex-row lg:items-center lg:justify-between">
        {/* Left Section */}
        <div className="flex min-w-0 items-start gap-3">
          {onBack && (
            <button
              type="button"
              onClick={onBack}
              className="mt-0.5 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 transition hover:border-gray-300 hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
              aria-label="Go back"
              title="Go back"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
          )}

          <div className="min-w-0">
            <h1 className="truncate text-xl font-bold tracking-tight text-gray-900 sm:text-2xl">
              {title}
            </h1>

            {subtitle && (
              <p className="mt-1 max-w-2xl text-sm text-gray-500">
                {subtitle}
              </p>
            )}
          </div>
        </div>

        {/* Right Section */}
        <div className="flex flex-wrap items-center gap-2">
          {children}

          {showRefresh && onRefresh && (
            <button
              type="button"
              onClick={onRefresh}
              disabled={isRefreshing}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-300 bg-white px-3.5 py-2 text-sm font-medium text-gray-700 shadow-sm transition hover:bg-gray-50 hover:text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <RefreshCw
                className={`h-4 w-4 ${
                  isRefreshing ? "animate-spin" : ""
                }`}
              />

              <span className="hidden sm:inline">
                {isRefreshing
                  ? "Refreshing..."
                  : refreshLabel}
              </span>
            </button>
          )}

          {showCreate && onCreate && (
            <button
              type="button"
              onClick={onCreate}
              className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 active:bg-blue-800"
            >
              <Plus className="h-4 w-4" />

              <span>{createLabel}</span>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default EnglishQuizHeader;